import { useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { DateRange } from '../../services/dashboardService';
import { dashboardService } from '../../services/dashboardService';
import { formatDate } from '../../utils/dateUtils';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface Props {
  dateRange: DateRange;
}

interface TrendData {
  date: string;
  created_count: number;
  closed_count: number;
}

export default function EnhancedTicketTrends({ dateRange }: Props) {
  const [trends, setTrends] = useState<TrendData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'line' | 'bar' | 'area'>('line');
  const { theme } = useTheme();
  const { t } = useTranslation();

  useEffect(() => {
    const fetchTrends = async () => {
      try {
        setIsLoading(true);
        const data = await dashboardService.getTicketTrends(dateRange);
        setTrends(data);
        setError(null);
      } catch (err) {
        setError(t('pageHeaders.dashboard.failedToLoadTrends', 'Failed to load ticket trends'));
        console.error('Error fetching trends:', err);
      } finally {
        setIsLoading(false);
      }
    };

    if (dateRange.startDate && dateRange.endDate) {
      fetchTrends();
    }
  }, [dateRange, t]);

  const chartData = {
    labels: trends.map(trend => formatDate(trend.date, 'MMM dd')),
    datasets: [
      {
        label: t('pageHeaders.dashboard.ticketsCreated', 'Tickets Created'),
        data: trends.map(trend => trend.created_count),
        borderColor: theme === 'dark' ? '#60A5FA' : '#3B82F6',
        backgroundColor: viewMode === 'area' 
          ? theme === 'dark' ? 'rgba(96, 165, 250, 0.1)' : 'rgba(59, 130, 246, 0.1)'
          : theme === 'dark' ? 'rgba(96, 165, 250, 0.8)' : 'rgba(59, 130, 246, 0.8)',
        fill: viewMode === 'area',
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: theme === 'dark' ? '#60A5FA' : '#3B82F6',
        pointBorderColor: theme === 'dark' ? '#1F2937' : '#FFFFFF',
        pointBorderWidth: 2,
      },
      {
        label: t('pageHeaders.dashboard.ticketsClosed', 'Tickets Closed'),
        data: trends.map(trend => trend.closed_count),
        borderColor: theme === 'dark' ? '#34D399' : '#10B981',
        backgroundColor: viewMode === 'area'
          ? theme === 'dark' ? 'rgba(52, 211, 153, 0.1)' : 'rgba(16, 185, 129, 0.1)'
          : theme === 'dark' ? 'rgba(52, 211, 153, 0.8)' : 'rgba(16, 185, 129, 0.8)',
        fill: viewMode === 'area',
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: theme === 'dark' ? '#34D399' : '#10B981',
        pointBorderColor: theme === 'dark' ? '#1F2937' : '#FFFFFF',
        pointBorderWidth: 2,
      },
      {
        label: t('pageHeaders.dashboard.netChange', 'Net Change'),
        data: trends.map(trend => trend.created_count - trend.closed_count),
        borderColor: theme === 'dark' ? '#F59E0B' : '#D97706',
        backgroundColor: theme === 'dark' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(217, 119, 6, 0.1)',
        fill: false,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 5,
        pointBackgroundColor: theme === 'dark' ? '#F59E0B' : '#D97706',
        pointBorderColor: theme === 'dark' ? '#1F2937' : '#FFFFFF',
        pointBorderWidth: 2,
        borderDash: [5, 5],
      }
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: theme === 'dark' ? '#E5E7EB' : '#374151',
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: theme === 'dark' ? '#1F2937' : '#FFFFFF',
        titleColor: theme === 'dark' ? '#E5E7EB' : '#374151',
        bodyColor: theme === 'dark' ? '#E5E7EB' : '#374151',
        borderColor: theme === 'dark' ? '#374151' : '#E5E7EB',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          afterLabel: function(context: any) {
            if (context.datasetIndex === 2) {
              const value = context.parsed.y;
              return value > 0 
                ? t('pageHeaders.dashboard.backlogIncreased', 'Backlog increased')
                : value < 0 
                ? t('pageHeaders.dashboard.backlogDecreased', 'Backlog decreased')
                : t('pageHeaders.dashboard.backlogStable', 'Backlog stable');
            }
            return '';
          }
        }
      },
    },
    scales: {
      x: {
        grid: {
          color: theme === 'dark' ? '#374151' : '#F3F4F6',
          drawBorder: false,
        },
        ticks: {
          color: theme === 'dark' ? '#9CA3AF' : '#6B7280',
          maxRotation: 45,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: theme === 'dark' ? '#374151' : '#F3F4F6',
          drawBorder: false,
        },
        ticks: {
          color: theme === 'dark' ? '#9CA3AF' : '#6B7280',
          callback: function(value: any) {
            return Math.floor(value).toString();
          },
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
  };

  // Calculate summary statistics
  const totalCreated = trends.reduce((sum, trend) => sum + trend.created_count, 0);
  const totalClosed = trends.reduce((sum, trend) => sum + trend.closed_count, 0);
  const netChange = totalCreated - totalClosed;
  const avgCreatedPerDay = trends.length > 0 ? (totalCreated / trends.length).toFixed(1) : '0';
  const avgClosedPerDay = trends.length > 0 ? (totalClosed / trends.length).toFixed(1) : '0';

  if (isLoading) {
    return (
      <div className={`rounded-lg shadow p-6 ${
        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className={`h-6 w-48 rounded mb-4 animate-pulse ${
          theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
        }`}></div>
        <div className={`h-80 w-full rounded animate-pulse ${
          theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
        }`}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rounded-lg shadow p-6 text-center ${
        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="text-red-500 mb-2">
          <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
          {error}
        </p>
      </div>
    );
  }



  return (
    <div className={`rounded-lg shadow p-6 flex-1 flex flex-col ${
      theme === 'dark' ? 'bg-gray-800' : 'bg-white'
    }`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h3 className={`text-lg font-semibold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            {t('pageHeaders.dashboard.ticketTrends', 'Ticket Trends')}
          </h3>
          <p className={`text-sm mt-1 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {t('pageHeaders.dashboard.trendsDescription', 'Daily ticket creation and resolution patterns')}
          </p>
        </div>
        
        {/* View Mode Toggle */}
        <div className="flex rounded-lg overflow-hidden border mt-4 sm:mt-0">
          {(['line', 'bar', 'area'] as const).map((mode) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`px-3 py-1 text-xs font-medium capitalize transition-colors ${
                viewMode === mode
                  ? theme === 'dark'
                    ? 'bg-blue-600 text-white'
                    : 'bg-blue-500 text-white'
                  : theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {mode}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className={`p-3 rounded-lg ${
          theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
        }`}>
          <div className={`text-xs font-medium ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {t('pageHeaders.dashboard.totalCreated', 'Total Created')}
          </div>
          <div className={`text-lg font-bold text-blue-500`}>
            {totalCreated}
          </div>
          <div className={`text-xs ${
            theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
          }`}>
            {avgCreatedPerDay}{t('dashboard.dayAverage', '/day avg')}
          </div>
        </div>
        
        <div className={`p-3 rounded-lg ${
          theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
        }`}>
          <div className={`text-xs font-medium ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {t('pageHeaders.dashboard.totalClosed', 'Total Closed')}
          </div>
          <div className={`text-lg font-bold text-green-500`}>
            {totalClosed}
          </div>
          <div className={`text-xs ${
            theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
          }`}>
            {avgClosedPerDay}{t('pageHeaders.dashboard.dayAverage', '/day avg')}
          </div>
        </div>
        
        <div className={`p-3 rounded-lg ${
          theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
        }`}>
          <div className={`text-xs font-medium ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {t('pageHeaders.dashboard.netChange', 'Net Change')}
          </div>
          <div className={`text-lg font-bold ${
            netChange > 0 ? 'text-orange-500' : netChange < 0 ? 'text-green-500' : 'text-gray-500'
          }`}>
            {netChange > 0 ? '+' : ''}{netChange}
          </div>
          <div className={`text-xs ${
            theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
          }`}>
            {netChange > 0 ? t('pageHeaders.dashboard.backlogGrew', 'Backlog grew') : netChange < 0 ? t('dashboard.backlogShrank', 'Backlog shrank') : t('dashboard.stable', 'Stable')}
          </div>
        </div>
        
        <div className={`p-3 rounded-lg ${
          theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
        }`}>
          <div className={`text-xs font-medium ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {t('pageHeaders.dashboard.efficiency', 'Efficiency')}
          </div>
          <div className={`text-lg font-bold ${
            totalCreated > 0 
              ? (totalClosed / totalCreated) >= 1 ? 'text-green-500' : 'text-orange-500'
              : 'text-gray-500'
          }`}>
            {totalCreated > 0 ? Math.round((totalClosed / totalCreated) * 100) : 0}%
          </div>
          <div className={`text-xs ${
            theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
          }`}>
            {t('pageHeaders.dashboard.resolutionRate', 'Resolution rate')}
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="flex-1 min-h-80">
        {trends.length > 0 ? (
          viewMode === 'bar' ? (
            <Bar data={chartData} options={chartOptions} />
          ) : (
            <Line data={chartData} options={chartOptions} />
          )
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('pageHeaders.dashboard.noTrendData', 'No trend data available for the selected date range')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
